import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Alert,
  AvatarGroup,
  Chip,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { Box } from "@mui/system";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FMEStatus from "../../../helpers/components/FMEStatus";
import { useSelector } from "react-redux";
import { addFmeToReceipt } from "../../../../../../../features/financeReceiptSlice";
import { useDispatch } from "react-redux";
import PairingSkeleton2forRows from "../../../helpers/components/TableSkeleton2forRows";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
}

const PairAddRecordsTable = ({ tableHeadings, rows }: Props) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [sortConfig, setSortConfig] = React.useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });
  const dispatch = useDispatch();
  const headCells = Object.entries(tableHeadings);
  const {
    selectedDateFilterPair,
    selectedStatusFilterPair,
    selectedLinkedFilterPair,
    FMEIdPair,
    activeReceiptSharedUid,
    allCurrencies,
    linkedFmesByReceipt,
    financeCompanyLoadingTable,
    selectedFinanceCompanyFilter,
  } = useSelector((state: any) => state.receipts);

  const linkedFmeUidSet = React.useMemo(() => {
    const set = new Set<string>();
    const activeFmes = linkedFmesByReceipt?.[activeReceiptSharedUid] || [];
    activeFmes.forEach((fme: any) => {
      if (fme.shared_uid) {
        set.add(fme.shared_uid);
      }
    });
    return set;
  }, [linkedFmesByReceipt, activeReceiptSharedUid]);

  const currencyMap = React.useMemo(() => {
    const map = new Map<string, string>();
    allCurrencies?.forEach((c: any) => {
      if (c.currency_name && c.currency_symbol) {
        map.set(c.currency_name.toLowerCase(), c.currency_symbol);
      }
    });
    return map;
  }, [allCurrencies]);

  const getCurrencySymbol = (currencyName?: string) =>
    currencyName ? (currencyMap.get(currencyName.toLowerCase()) ?? "") : "";

  const sortRows = (
    data: any[],
    key: string | null,
    direction: "asc" | "desc",
  ) => {
    if (!key) return data;

    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (key === "month") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (typeof aVal === "number" && typeof bVal === "number") {
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const isBetweenDates = (date: string, from: string, to: string) => {
    const d = new Date(date).getTime();
    return d >= new Date(from).getTime() && d <= new Date(to).getTime();
  };
  const formatDateDDMMYYYY = (dateString: string) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const filteredRows = rows.filter((row) => {
    if (linkedFmeUidSet.has(row.shared_uid)) {
      return false;
    }
    const isDateFilterEnabled =
      Array.isArray(selectedDateFilterPair) &&
      selectedDateFilterPair.length === 2 &&
      selectedDateFilterPair[0] &&
      selectedDateFilterPair[1];

    const matchesDate = isDateFilterEnabled
      ? isBetweenDates(
          row.month,
          selectedDateFilterPair[0],
          selectedDateFilterPair[1],
        )
      : true;

    const matchesStatus =
      Array.isArray(selectedStatusFilterPair) &&
      selectedStatusFilterPair.length > 0
        ? selectedStatusFilterPair.includes(row.fme_status)
        : true;

    const matchesLinked =
      Array.isArray(selectedLinkedFilterPair) &&
      selectedLinkedFilterPair.length > 0
        ? selectedLinkedFilterPair.includes(row.linked)
        : true;

    const matchesFMEId = FMEIdPair ? row.id === FMEIdPair : true;

    return matchesDate && matchesStatus && matchesLinked && matchesFMEId;
  });
  const sortedRows = React.useMemo(() => {
    return sortRows(filteredRows, sortConfig.key, sortConfig.direction);
  }, [filteredRows, sortConfig]);

  React.useEffect(() => {
    setPage(0);
  }, [
    selectedStatusFilterPair,
    selectedLinkedFilterPair,
    selectedDateFilterPair,
    selectedFinanceCompanyFilter,
  ]);
  return (
    <>
      {financeCompanyLoadingTable ? (
        <PairingSkeleton2forRows />
      ) : (
        <Paper sx={{ pt: 2, boxShadow: "none" }}>
          <TableContainer className="receiptsTable">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headCells.map(([key, value], index) => (
                    <TableCell key={index} align="left">
                      <TableSortLabel
                        active={sortConfig.key === key}
                        direction={
                          sortConfig.key === key ? sortConfig.direction : "asc"
                        }
                        onClick={() => {
                          setSortConfig((prev) => ({
                            key,
                            direction:
                              prev.key === key && prev.direction === "asc"
                                ? "desc"
                                : "asc",
                          }));
                          setPage(0); // reset pagination on sort
                        }}
                      >
                        {value}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={headCells.length}
                      align="center"
                      sx={{ py: 4, color: "text.secondary" }}
                    >
                      No data to display
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <React.Fragment key={row.cron_id}>
                        <TableRow
                          sx={{
                            "& > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          {headCells.map(([key], cellIndex) => (
                            <TableCell key={cellIndex} align="left">
                              {key === "id" ? (
                                <div className="chip-data">
                                  <>
                                    {/* Display first chip */}
                                    <Chip
                                      data-tracker="588"
                                      label={row[key] ? row[key] : "N/A"}
                                      onClick={() => {
                                        dispatch(
                                          addFmeToReceipt({
                                            receiptSharedUid:
                                              activeReceiptSharedUid,
                                            fme: {
                                              id: row.id,
                                              shared_uid: row.shared_uid,
                                              ...row,
                                            },
                                          }),
                                        );
                                      }}
                                      sx={{
                                        backgroundColor: "#ffffff",
                                        color: "rgba(0, 0, 0, 0.87)",
                                        borderRadius: "5px",
                                        boxShadow: "1px 1px 2px 1px #00000030",
                                        height: "26px",
                                        fontWeight: "500",
                                        cursor: "pointer",
                                        border: "1px solid #ffffff",
                                        "&:hover": {
                                          backgroundColor: "transparent",
                                          color: "#9c27b0",
                                          border: "1px solid #9c27b0",
                                        },
                                      }}
                                    />
                                  </>
                                </div>
                              ) : key === "month" ? (
                                <>{row[key] ? formatDateDDMMYYYY(row[key]) : "N/A"}</>
                              ) : key === "invoice_total_income" ? (
                                <>
                                  <Box display="flex" alignItems="center">
                                    {row[key] ?? "N/A"}
                                    <Box
                                      ml={0.5}
                                      fontWeight={500}
                                      color="rgba(0,0,0,0.56)"
                                    >
                                      {getCurrencySymbol(row.currency)}
                                    </Box>
                                  </Box>
                                </>
                              ) : key === "manual_invoice_total_income" ? (
                                <>
                                  <Box display="flex" alignItems="center">
                                    {row[key] ?? "N/A"}
                                    <Box
                                      ml={0.5}
                                      fontWeight={500}
                                      color="rgba(0,0,0,0.56)"
                                    >
                                      {getCurrencySymbol(row.currency)}
                                    </Box>
                                  </Box>
                                </>
                              ) : key === "linked" ? (
                                <>
                                  <div className="AlertItems">
                                    {row[key] === "Yes" ? (
                                      <Alert
                                        icon={
                                          <CheckCircleOutlineIcon
                                            fontSize="inherit"
                                            sx={{ color: "rgba(30, 70, 32, 1)" }}
                                          />
                                        }
                                        severity="success"
                                        sx={{
                                          border:
                                            "1px solid rgba(46, 125, 50, 0.5)",
                                          padding: "0px 5px",
                                          display: "inline-flex",
                                        }}
                                      >
                                        Yes
                                      </Alert>
                                    ) : row[key] === "No" ? (
                                      <Alert
                                        icon={
                                          <ErrorOutlineIcon
                                            fontSize="inherit"
                                            sx={{ color: "rgba(95, 33, 32, 1)" }}
                                          />
                                        }
                                        severity="error"
                                        sx={{
                                          border:
                                            "1px solid rgba(211, 47, 47, 0.5)",
                                          padding: "0px 5px",
                                          display: "inline-flex",
                                        }}
                                      >
                                        No
                                      </Alert>
                                    ) : (
                                      "N/A"
                                    )}
                                  </div>
                                </>
                              ) : key === "fme_status" ? (
                                <>{row[key] ? <FMEStatus status={row[key]} /> : "N/A"}</>
                              ) : key === "account" ? (
                                <>{row[key] ? row[key] : ""}</>
                              ) : key === "brands" ? (
                                <div className="chip-data">
                                  {Array.isArray(row?.brands) &&
                                    row.brands.length > 0 && (
                                      <>
                                        <Chip
                                          label={row.brands[0]}
                                          sx={{
                                            backgroundColor: "#7B1FA2",
                                            color: "#ffffff",
                                            height: "26px",
                                          }}
                                        />
                                        {row.brands.length > 1 && (
                                          <Chip
                                            label={row.brands[1]}
                                            sx={{
                                              backgroundColor: "#7B1FA2",
                                              color: "#ffffff",
                                              height: "26px",
                                              ml: "1px",
                                            }}
                                          />
                                        )}
                                        {row.brands.length > 2 && (
                                          <AvatarGroup
                                            total={row.brands.length - 2}
                                          />
                                        )}
                                      </>
                                    )}
                                </div>
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={sortedRows?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                  {
                    display: "none",
                  },
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-toolbar": {
                  paddingLeft: "16px",
                },
              }}
            />
            <Pagination
              count={Math.ceil(sortedRows?.length / rowsPerPage)}
              page={page + 1}
              showFirstButton
              showLastButton
              className="pagination-button"
              color="secondary"
              size="small"
              sx={{
                ".css-wjh20t-MuiPagination-ul": {
                  justifyContent: "center",
                },
              }}
              onChange={(_, value) => handleChangePage(null, value - 1)}
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={sortedRows?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              classes={{
                displayedRows: "hidden-displayed-rows", // Apply the custom class
              }}
              sx={{
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-input": {
                  marginRight: "15px",
                },
              }}
            />
          </Stack>
        </Paper>
      )}
    </>
  );
};

export default PairAddRecordsTable;
